import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { DriverAdminTabs } from '../../components/admin/DriverAdminTabs';
import { Link2, Car, User, Loader2, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
interface Driver {
  id: number;
  full_name: string;
  phone: string;
}
interface Vehicle {
  id: number;
  plate: string;
  vehicle_type: string;
  brand: string | null;
  model: string | null;
}
interface Assignment {
  id: number;
  assigned_at: string;
  drivers: {
    full_name: string;
    phone: string;
  };
  vehicles: {
    plate: string;
    vehicle_type: string;
  };
}
export function AssignmentsPage() {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [currentVehicle, setCurrentVehicle] = useState<Vehicle | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  useEffect(() => {
    fetchInitialData();
  }, []);
  useEffect(() => {
    if (selectedDriverId) {
      fetchCurrentVehicleForDriver(parseInt(selectedDriverId));
    } else {
      setCurrentVehicle(null);
    }
  }, [selectedDriverId]);
  const fetchInitialData = async () => {
    setLoadingData(true);
    const [driversRes, vehiclesRes, assignmentsRes] = await Promise.all([supabase.from('drivers').select('id, full_name, phone').eq('is_active', true).order('full_name'), supabase.from('vehicles').select('id, plate, vehicle_type, brand, model').eq('is_active', true).order('plate'), supabase.from('driver_vehicle_assignments').select('id, assigned_at, drivers(full_name, phone), vehicles(plate, vehicle_type)').eq('is_active', true).order('assigned_at', {
      ascending: false
    })]);
    if (driversRes.data) setDrivers(driversRes.data);
    if (vehiclesRes.data) setVehicles(vehiclesRes.data);
    if (assignmentsRes.data) setAssignments(assignmentsRes.data as any);
    setLoadingData(false);
  };
  const fetchCurrentVehicleForDriver = async (driverId: number) => {
    try {
      const {
        data,
        error
      } = await supabase.rpc('get_active_vehicle_for_driver', {
        p_driver_id: driverId
      });
      if (!error && data) {
        setCurrentVehicle(Array.isArray(data) ? data[0] : data);
      } else {
        setCurrentVehicle(null);
      }
    } catch (err) {
      setCurrentVehicle(null);
    }
  };
  const handleAssign = async () => {
    if (!selectedDriverId || !selectedVehicleId) return;
    setIsAssigning(true);
    setError(null);
    setSuccess(null);
    try {
      const {
        error: rpcError
      } = await supabase.rpc('assign_vehicle_to_driver', {
        p_driver_id: parseInt(selectedDriverId),
        p_vehicle_id: parseInt(selectedVehicleId)
      });
      if (rpcError) throw rpcError;
      setSuccess('Vehículo asignado correctamente.');
      await fetchInitialData();
      await fetchCurrentVehicleForDriver(parseInt(selectedDriverId));
      setSelectedVehicleId('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('[AssignVehicle] Error:', err);
      setError(err.message || 'Error al asignar el vehículo.');
    } finally {
      setIsAssigning(false);
    }
  };
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-3243" data-mp="36e0ofmrqw04n">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-id="element-3244" data-mp="7qv3jzcnqmkq">
        <DriverAdminTabs data-id="element-3245" data-mp="2yuunhem0jc0" />

        {/* Header */}
        <div className="flex items-center gap-3" data-id="element-3246" data-mp="2y1ytbh6ve8n">
          <div className="w-12 h-12 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-3247" data-mp="3jm7ufg4hcgl8">
            <Link2 className="w-6 h-6 text-[#006847]" data-id="element-3248" data-mp="3omvpccvtxhkq" />
          </div>
          <div data-id="element-3249" data-mp="39p391iigkp5h">
            <h1 className="text-2xl font-bold" data-id="element-3250" data-mp="39pcwozg16k80">Asignar Vehículos</h1>
            <p className="text-white/40 text-sm" data-id="element-3251" data-mp="3eno6ja6bsuq8">
              Vincula conductores con vehículos activos
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-3252" data-mp="68cyqrfufpxz">
          {/* Assignment Form */}
          <div className="lg:col-span-5 space-y-6" data-id="element-3253" data-mp="b6ehfb9cxn8x">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 shadow-2xl" data-id="element-3254" data-mp="3snp86ruf066r">
              <h2 className="text-lg font-bold mb-6" data-id="element-3255" data-mp="1guxe29wrnch">Nueva Asignación</h2>

              <div className="space-y-5" data-id="element-3256" data-mp="1f1n8d036kzq">
                {/* Driver Select */}
                <div data-id="element-3257" data-mp="p55owetpsao3">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3258" data-mp="p4w0gvobibki">
                    Conductor
                  </label>
                  <div className="relative" data-id="element-3259" data-mp="2emjenwau8ezv">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" data-id="element-3260" data-mp="29orbu78nn8gw" />
                    <select value={selectedDriverId} onChange={e => setSelectedDriverId(e.target.value)} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base text-white focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847] appearance-none" data-id="element-3261" data-mp="2odns2rkv7rsl">
                      <option value="" data-id="element-3262" data-mp="24poj9m9v2upt">Seleccionar conductor...</option>
                      {drivers.map(d => <option key={d.id} value={d.id} data-id="element-3263" data-mp="20obezgmf2zl0">
                          {d.full_name} ({d.phone})
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* Current Vehicle Info */}
                {selectedDriverId && currentVehicle && <div className="bg-[#006847]/10 border border-[#006847]/20 rounded-xl p-4 flex items-center gap-3" data-id="element-3264" data-mp="28rsx9wxy9cpr">
                    <Car className="w-5 h-5 text-[#00a870]" data-id="element-3265" data-mp="2dtibflggy6i2" />
                    <div data-id="element-3266" data-mp="2irju459zg3t0">
                      <p className="text-xs text-[#00a870] font-bold uppercase tracking-wider" data-id="element-3267" data-mp="2ira086lthci1">
                        Vehículo actual
                      </p>
                      <p className="text-sm font-mono text-white mt-0.5" data-id="element-3268" data-mp="1q2md05hicd1u">
                        {currentVehicle.plate}{' '}
                        <span className="font-sans text-white/50" data-id="element-3269" data-mp="1v1fo5r9bnryu">
                          ({currentVehicle.vehicle_type})
                        </span>
                      </p>
                    </div>
                  </div>}
                {selectedDriverId && !currentVehicle && <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3" data-id="element-3270" data-mp="17b39zynhgyzt">
                    <Car className="w-5 h-5 text-white/30" data-id="element-3271" data-mp="1bau7smbfgfdu" />
                    <p className="text-sm text-white/50" data-id="element-3272" data-mp="13scvk8c62mh6">
                      Sin vehículo asignado actualmente.
                    </p>
                  </div>}

                {/* Vehicle Select */}
                <div data-id="element-3273" data-mp="ur6bjpab6tj6">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-3274" data-mp="uuu1yfkgizto">
                    Vehículo a asignar
                  </label>
                  <div className="relative" data-id="element-3275" data-mp="kw4metg19rgl">
                    <Car className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" data-id="element-3276" data-mp="g3tjdhpzxpjt" />
                    <select value={selectedVehicleId} onChange={e => setSelectedVehicleId(e.target.value)} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base text-white focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847] appearance-none" data-id="element-3277" data-mp="b4gzn5uf8ylg">
                      <option value="" data-id="element-3278" data-mp="1l1nh0n31ewyg">Seleccionar vehículo...</option>
                      {vehicles.map(v => <option key={v.id} value={v.id} data-id="element-3279" data-mp="1q19umxms2f7s">
                          {v.plate} - {v.vehicle_type}{' '}
                          {v.brand ? `(${v.brand})` : ''}
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* Messages */}
                {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-3280" data-mp="129cj77n8pp77">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-3281" data-mp="tb0wvhtepdkk" />
                    <div className="text-sm whitespace-pre-line" data-id="element-3282" data-mp="t8o7ijlbk0xh">{error}</div>
                  </div>}
                {success && <div className="p-4 bg-[#006847]/10 border border-[#006847]/20 rounded-xl flex items-start gap-3 text-[#00a870]" data-id="element-3283" data-mp="jdcrlxy6cno5">
                    <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" data-id="element-3284" data-mp="oamr66fa2fpp" />
                    <div className="text-sm" data-id="element-3285" data-mp="4qdzs1venr8w">{success}</div>
                  </div>}

                {/* Submit */}
                <button onClick={handleAssign} disabled={isAssigning || !selectedDriverId || !selectedVehicleId} className="w-full py-5 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-black text-lg rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95 mt-4" data-id="element-3286" data-mp="3vy0csl40l62f">
                  {isAssigning ? <Loader2 className="w-6 h-6 animate-spin" data-id="element-3287" data-mp="xdqpgkwv4f6f" /> : <>
                      <Link2 className="w-6 h-6" data-id="element-3288" data-mp="sfyu1gx0eaxh" />
                      Asignar Vehículo
                    </>}
                </button>
              </div>
            </div>
          </div>

          {/* Active Assignments Table */}
          <div className="lg:col-span-7" data-id="element-3289" data-mp="3clbf1t1s73wg">
            <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden shadow-2xl" data-id="element-3290" data-mp="3cm4gcehz1177">
              <div className="p-6 border-b border-white/5" data-id="element-3291" data-mp="37s08lt0vsp52">
                <h2 className="text-lg font-bold" data-id="element-3292" data-mp="32w1r8pdz07e5">Asignaciones Activas</h2>
              </div>

              {loadingData ? <div className="flex justify-center py-12" data-id="element-3293" data-mp="2xxqg8bbxvquc">
                  <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-3294" data-mp="3vjxk1j0f5f5n" />
                </div> : assignments.length === 0 ? <div className="text-center py-12" data-id="element-3295" data-mp="3qldznn1h4bfe">
                  <Link2 className="w-12 h-12 text-white/10 mx-auto mb-3" data-id="element-3296" data-mp="3lkq912h5pwlp" />
                  <p className="text-white/40" data-id="element-3297" data-mp="3hk6bhxso8qz1">No hay asignaciones activas</p>
                </div> : <div className="overflow-x-auto" data-id="element-3298" data-mp="2a6l1jzhqj4lm">
                  <table className="w-full text-left text-sm" data-id="element-3299" data-mp="2a4892secpbub">
                    <thead className="bg-[#0B0B0B] text-white/40 uppercase tracking-wider text-[10px]" data-id="element-3300" data-mp="263objnpv867n">
                      <tr data-id="element-3301" data-mp="lo00pwz34zp8">
                        <th className="px-6 py-4 font-bold" data-id="element-3302" data-mp="lklz4qy8wuiu">Conductor</th>
                        <th className="px-6 py-4 font-bold" data-id="element-3303" data-mp="pp3iip0p15t3">Vehículo</th>
                        <th className="px-6 py-4 font-bold text-right" data-id="element-3304" data-mp="un4z93anct1e">
                          Asignado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5" data-id="element-3305" data-mp="14dpvik2ullh5">
                      {assignments.map(a => <tr key={a.id} className="hover:bg-white/5 transition-colors" data-id="element-3306" data-mp="19bhxy814cpzl">
                          <td className="px-6 py-4" data-id="element-3307" data-mp="1icza5fbeo7j0">
                            <div className="font-bold text-white" data-id="element-3308" data-mp="1ibo4tb2barps">
                              {a.drivers?.full_name}
                            </div>
                            <div className="text-white/40 text-xs mt-0.5" data-id="element-3309" data-mp="3k42mqa4uvw67">
                              {a.drivers?.phone}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-3310" data-mp="mar6dj0buoeu">
                            <div className="font-mono font-bold text-white" data-id="element-3311" data-mp="mc2iq77u94hk">
                              {a.vehicles?.plate}
                            </div>
                            <div className="text-white/40 text-xs mt-0.5 uppercase" data-id="element-3312" data-mp="vbpeodhsti38">
                              {a.vehicles?.vehicle_type}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right text-white/40" data-id="element-3313" data-mp="15a550e904nbo">
                            <div className="flex items-center justify-end gap-1.5" data-id="element-3314" data-mp="1j10ensrbr8gb">
                              <Clock className="w-3.5 h-3.5" data-id="element-3315" data-mp="1j2urskwctw9u" />
                              {new Date(a.assigned_at).toLocaleDateString()}
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}